import { message, Popconfirm, Tooltip } from 'antd'
import * as React from 'react'
import { useState } from 'react'

import {
    RichTextEditor,
    RichTextEditorProps,
    RichTextViewer,
    RichTextViewerProps
} from './'
import {
    EditableRichTextOptions,
    EditableRichTextProps,
    EditorMode,
    UseEditableRichTextProps,
    TitlePosition
} from './types'

import * as styles from './EditableRichText.module.less'

const editorModeDefault: EditorMode = 'view'
const UserCantEditMessage = `You don't have the permission to edit this.`

export const useEditor = (
    initialEditorMode?: EditorMode,
    options?: EditableRichTextOptions
) => {
    const [editorMode, setEditorMode] = useState<EditorMode>(
        initialEditorMode || 'view'
    )
    return {
        editorMode,
        setEditorMode
    }
}

export const useEditableRichText = (props: UseEditableRichTextProps) => {
    const { actions, options, state, title, titlePosition } = props

    // initial values
    const canEdit: boolean =
        options && options.canEdit !== undefined ? options.canEdit : false
    const initialContent = state ? state.content : ''
    let initialEditorMode: EditorMode = initialContent === '' ? 'view' : 'edit'
    initialEditorMode =
        options && options.initialMode ? options.initialMode : initialEditorMode
    const [editorMode, setEditorMode] = useState<EditorMode>(initialEditorMode)

    const [content, setContent] = useState(() => initialContent)

    React.useEffect(() => {
        setContent(state?.content)
    }, [state && state.content])

    const onSave = (value: string) => {
        if (actions && actions.onSave) {
            const newContentCleaned = cleanContent(value)
            setContent(value)
            setEditorMode('view')
            actions.onSave(newContentCleaned)
        } else {
            console.warn('Please provide an "onSave" method')
        }
    }

    const setToViewMode = () => {
        setEditorMode('view')
    }

    const setToEditMode = () => {
        if (canEdit) {
            setEditorMode('edit')
        } else {
            message.error(UserCantEditMessage)
        }
    }

    return {
        actions: {
            onSave,
            setToEditMode,
            setToViewMode
        },
        options,
        state: {
            content,
            editorMode
        },
        title,
        titlePosition
    }
}

/**
 * const props = useEditableRichText(editableRichTextProps)
 * onst { setToEditMode } = props.actions
 * <EditableRichText {...props} />
 * <Button onClick={setToEditMode}>Edit</Button>
 * @param props
 */
export const EditableRichText: React.FC<EditableRichTextProps> = (props) => {
    const { actions, options, state } = props
    const [popConfirmVisible, setPopConfirmVisible] = useState(false)

    // default props
    const canEdit: boolean =
        options && options.canEdit !== undefined ? options.canEdit : false
    const titlePosition: TitlePosition = props.titlePosition || 'left'

    // Edit on click
    const onRichTextViewerClick = () => {
        if (options?.editOnClick && options.canEdit) {
            actions.setToEditMode && actions.setToEditMode()
        }
    }

    const onCancel = (newContent: string) => {
        if (cleanContent(newContent) !== state.content) {
            setPopConfirmVisible(true)
        } else {
            actions.setToViewMode && actions.setToViewMode()
        }
    }

    const onCancelPop = () => {
        setPopConfirmVisible(false)
    }
    const onConfirm = () => {
        setPopConfirmVisible(false)
        actions.setToViewMode && actions.setToViewMode()
    }

    const richTextViewerProps: RichTextViewerProps = {
        content: state.content,
        getTributeComponent: options && options.getTributeComponent
    }

    let richTextViewer = (
        <div className={styles.viewer} onClick={onRichTextViewerClick}>
            <RichTextViewer {...richTextViewerProps} />
        </div>
    )

    const richTextEditor = (
        <Popconfirm
            title="Are you sure? All changes will be lost."
            visible={popConfirmVisible}
            onConfirm={onConfirm}
            onCancel={onCancelPop}
            okText="Yes"
            cancelText="No"
        >
            <div className={styles.editor}>
                <RichTextEditor
                    content={state.content}
                    getTributeComponent={options && options.getTributeComponent}
                    onCancel={onCancel}
                    onSave={actions.onSave}
                    tributes={options && options.tributes}
                />
            </div>
        </Popconfirm>
    )

    if (!canEdit) {
        richTextViewer = (
            <Tooltip mouseEnterDelay={0.3} title={UserCantEditMessage}>
                {richTextViewer}
            </Tooltip>
        )
    }

    const richText =
        state.editorMode === 'edit' ? richTextEditor : richTextViewer

    let editableRichTextWrapperClassName = styles.editableRichTextWrapper
    if (props.title) {
        editableRichTextWrapperClassName =
            titlePosition === 'left'
                ? styles.editableRichTextWrapperTitleLeft
                : styles.editableRichTextWrapperTitleTop
    }

    const title = props.title ? (
        <div className={styles.title}>{props.title}</div>
    ) : null
    return (
        <div className={editableRichTextWrapperClassName}>
            {title}
            {richText}
        </div>
    )
}

const cleanContent = (content: string) => {
    return content.replace(
        '<p data-f-id="pbf" style="text-align: center; font-size: 14px; margin-top: 30px; opacity: 0.65; font-family: sans-serif;">Powered by <a href="https://www.froala.com/wysiwyg-editor?pb=1" title="Froala Editor">Froala Editor</a></p>',
        ''
    )
}
